/**
 * Android Platform Implementation
 * Contains all Android bridge generation logic for React Native modules
 */

import fs from "fs-extra";
import path from "path";
import pc from "picocolors";
import type { ComponentConfig } from "../main-index";
import type {
  ModuleConstant,
  ModuleEvent,
  ModuleMethod,
  ParsedModule,
} from "../parser";
import type { ScannedModule } from "../scanner";
import {
  createKotlinBridgeGenerator,
  createKotlinCodeEmitter,
  createKotlinGeneratorSuite,
  createKotlinMethodGenerator,
  createKotlinTypeMapper,
} from "../syntax/kotlin";
import type { AndroidPlatformGeneratorInterface } from "./platform-interface";

export interface BridgeGenerationResult {
  generatedFiles: string[];
  warnings: string[];
}

export class AndroidPlatformGenerator
  implements AndroidPlatformGeneratorInterface
{
  private config: ComponentConfig;
  private currentOutputDir: string = "";
  private scannedModules: ScannedModule[] = [];
  private typeMapper: ReturnType<typeof createKotlinTypeMapper>;
  private bridgeGenerator: ReturnType<typeof createKotlinBridgeGenerator>;

  constructor(config: ComponentConfig) {
    this.config = config;
    this.typeMapper = createKotlinTypeMapper();
    const methodGenerator = createKotlinMethodGenerator({
      typeMapper: this.typeMapper,
    });
    const codeEmitter = createKotlinCodeEmitter();
    this.bridgeGenerator = createKotlinBridgeGenerator({
      codeEmitter,
      methodGenerator,
    });
  }

  // (moved to utils/type-helpers.ts)

  getPlatformName(): "android" {
    return "android";
  }

  supports(target: string): boolean {
    return target === "android" || target === "java" || target === "kotlin";
  }

  /**
   * Main method - Generates Android bridge files
   */
  async generateBridge(
    modules: ParsedModule[],
    scannedModules?: ScannedModule[]
  ): Promise<string[]> {
    // Store scanned modules for use in build.gradle generation
    if (scannedModules) {
      this.scannedModules = scannedModules;
    }
    try {
      // Generate files in configured Android output directory
      const androidBrickDir = path.join(
        this.config.projectRoot,
        this.config.outputAndroid
      );
      const kotlinOutputDir = path.join(
        androidBrickDir,
        "src",
        "main",
        "kotlin"
      );
      this.currentOutputDir = androidBrickDir;
      const generatedFiles: string[] = [];

      await fs.ensureDir(kotlinOutputDir);

      // Generate Android bridge files like iOS
      try {
        const bridgeFile = await this.generateAndroidBridge(
          modules,
          kotlinOutputDir
        );
        generatedFiles.push(bridgeFile);
      } catch (error) {
        throw new Error(
          `Failed in generateAndroidBridge: ${(error as Error).message}`
        );
      }

      // Generate Kotlin implementation
      try {
        const implFile = await this.generateKotlinImplementation(
          modules,
          kotlinOutputDir
        );
        generatedFiles.push(implFile);
      } catch (error) {
        throw new Error(
          `Failed in generateKotlinImplementation: ${(error as Error).message}`
        );
      }

      // Note: Spec interfaces are generated only in library mode
      // They should be imported from the library packages, not generated in app projects

      // Generate Kotlin data classes for complex types
      try {
        const dataClassFiles = await this.generateKotlinDataClasses(modules);
        generatedFiles.push(...dataClassFiles);
      } catch (error) {
        throw new Error(
          `Failed in generateKotlinDataClasses: ${(error as Error).message}`
        );
      }

      // Generate local module types in .brick/src/main/java/com/brickmodule/codegen/ directory
      try {
        const localTypeFiles = await this.generateLocalModuleTypes(
          modules,
          androidBrickDir
        );
        generatedFiles.push(...localTypeFiles);
      } catch (error) {
        throw new Error(
          `Failed in generateLocalModuleTypes: ${(error as Error).message}`
        );
      }

      // Note: Library types and interfaces are now generated by each library in library mode
      // This ensures proper separation between user projects and library packages

      // Generate BrickModuleBase.kt
      try {
        await this.generateBrickModuleBase(kotlinOutputDir);
        generatedFiles.push(path.join(kotlinOutputDir, "BrickModuleBase.kt"));
      } catch (error) {
        throw new Error(
          `Failed in generateBrickModuleBase: ${(error as Error).message}`
        );
      }

      // Generate build.gradle for the generated code
      try {
        const gradleFile = await this.generateBuildGradle();
        generatedFiles.push(gradleFile);
      } catch (error) {
        throw new Error(
          `Failed in generateBuildGradle: ${(error as Error).message}`
        );
      }

      // BrickModulePackage is already provided by brick-module library
      // It dynamically loads the generated BrickModule class

      // Skip manual C++ generation - React Native Codegen will generate proper JNI files

      // Note: Files are kept in android/.brick directory and included via brick_modules.gradle
      return generatedFiles;
    } catch (error) {
      throw new Error(
        `Failed to generate Android bridge: ${(error as Error).message}`
      );
    }
  }

  isAvailable(): boolean {
    return true;
  }

  /**
   * Generates Kotlin data classes for complex types and interfaces
   */
  private async generateKotlinDataClasses(
    modules: ParsedModule[]
  ): Promise<string[]> {
    const generatedFiles: string[] = [];
    const allMethods = this.getAllMethods(modules);
    const processedTypes = new Set<string>();

    // First, generate data classes for TypeScript interfaces (User, UserResult, etc.)
    for (const module of modules) {
      const dataClasses: string[] = [];

      // Extract interface definitions from module AST
      const interfaces = this.extractInterfaceDefinitions(module);

      for (const interfaceDef of interfaces) {
        if (!processedTypes.has(interfaceDef.name)) {
          const dataClass =
            this.generateKotlinDataClassFromInterface(interfaceDef);
          dataClasses.push(dataClass);
          processedTypes.add(interfaceDef.name);
        }
      }

      // Write interface data classes to file if any exist
      if (dataClasses.length > 0) {
        const content = `// This file is automatically generated by brick-codegen
// Do not edit manually - regenerate using: brick-codegen

package com.brickmodule.codegen

import com.google.gson.annotations.SerializedName

${dataClasses.join("\n\n")}
`;

        const outputPath = path.join(
          this.currentOutputDir,
          `${module.moduleName}Types.kt`
        );
        await fs.writeFile(outputPath, content);
        this.logGenerated(outputPath);
        generatedFiles.push(outputPath);
      }
    }

    for (const { method, module } of allMethods) {
      const dataClasses: string[] = [];

      // Generate data classes for parameters
      for (const param of method.params) {
        if (param.type.startsWith("{") && param.type.endsWith("}")) {
          const className = `${module.moduleName}${this.capitalize(
            method.name
          )}${this.capitalize(param.name)}`;
          if (!processedTypes.has(className)) {
            const dataClass = this.generateKotlinDataClass(className);
            dataClasses.push(dataClass);
            processedTypes.add(className);
          }
        } else if (param.type.endsWith("[]")) {
          // Handle array element types
          const elementType = param.type.slice(0, -2);
          if (elementType.startsWith("{") && elementType.endsWith("}")) {
            const className = `${module.moduleName}${this.capitalize(
              method.name
            )}${this.capitalize(param.name)}Element`;
            if (!processedTypes.has(className)) {
              const dataClass = this.generateKotlinDataClass(className);
              dataClasses.push(dataClass);
              processedTypes.add(className);
            }
          }
        }
      }

      // Generate data classes for return types
      let actualReturnType = method.returnType;
      if (
        actualReturnType.startsWith("Promise<") &&
        actualReturnType.endsWith(">")
      ) {
        actualReturnType = actualReturnType.slice(8, -1);
      }

      if (actualReturnType.startsWith("{") && actualReturnType.endsWith("}")) {
        const className = `${module.moduleName}${this.capitalize(
          method.name
        )}Response`;
        if (!processedTypes.has(className)) {
          const dataClass = this.generateKotlinDataClass(className);
          dataClasses.push(dataClass);
          processedTypes.add(className);
        }
      }

      // Write data classes for this module if any exist
      if (dataClasses.length > 0) {
        const content = `// This file is automatically generated by brick-codegen
// Do not edit manually - regenerate using: brick-codegen

package com.brickmodule.codegen

import com.google.gson.annotations.SerializedName

${dataClasses.join("\n\n")}
`;

        const outputPath = path.join(
          this.currentOutputDir,
          `${module.moduleName}${this.capitalize(method.name)}Types.kt`
        );
        await fs.writeFile(outputPath, content);
        this.logGenerated(outputPath);
        generatedFiles.push(outputPath);
      }
    }

    return generatedFiles;
  }

  /**
   * Generates a single Kotlin data class from TypeScript inline object type
   * Note: This is a fallback for inline objects. Most types should come from AST-based interfaces.
   */
  private generateKotlinDataClass(className: string): string {
    // For now, use a simplified approach for inline object types
    // In practice, most complex types should be extracted as interfaces by parser.ts
    console.warn(
      `[Android] Generating data class for inline object type: ${className}. Consider extracting as interface.`
    );

    // Simple fallback implementation - in practice, the parser should extract all interfaces
    const simpleProperty = `    @SerializedName("data")
    val data: Any`;

    return `data class ${className}(
${simpleProperty}
)`;
  }

  /**
   * Gets available brick modules from scanned modules
   * Uses the scanned modules from Scanner instead of re-scanning
   */
  private async detectBrickModules(): Promise<
    { npmName: string; gradleProjectName: string }[]
  > {
    const brickModules: { npmName: string; gradleProjectName: string }[] = [];

    try {
      // Use the scanned modules that were passed from main-index.ts
      for (const module of this.scannedModules) {
        // Only process modules that have Android implementation
        if (module.hasAndroidImplementation) {
          // Convert npm package name to valid Gradle project name
          // Replace @ with empty and / with _
          const gradleProjectName = module.name
            .replace(/^@/, "")
            .replace(/\//g, "_");

          brickModules.push({
            npmName: module.name,
            gradleProjectName: gradleProjectName,
          });
        }
      }
    } catch (error) {
      console.warn(
        `[ANDROID] Warning: Could not process brick modules: ${error}`
      );
    }

    return brickModules;
  }

  /**
   * Generate build.gradle
   */
  private async generateBuildGradle(): Promise<string> {
    const brickModules = await this.detectBrickModules();
    const content = this.bridgeGenerator.generateBuildGradle(brickModules);

    const outputPath = path.join(this.currentOutputDir, "build.gradle");
    await fs.writeFile(outputPath, content);
    this.logGenerated(outputPath);

    return outputPath;
  }

  // Type mapping utilities
  private mapTypeScriptToKotlin(tsType: string): string {
    return this.typeMapper.mapTypeScriptToKotlin(tsType);
  }

  // Helper methods
  private getAllMethods(modules: ParsedModule[]): {
    method: ModuleMethod;
    module: ParsedModule;
  }[] {
    const methods: {
      method: ModuleMethod;
      module: ParsedModule;
    }[] = [];

    if (!modules || !Array.isArray(modules)) {
      return methods;
    }

    for (const module of modules) {
      if (!module || !module.methods || !Array.isArray(module.methods)) {
        continue;
      }

      for (const method of module.methods) {
        methods.push({
          method,
          module,
        });
      }
    }

    return methods;
  }

  private getAllEvents(
    modules: ParsedModule[]
  ): Array<ModuleEvent & { moduleName: string }> {
    const events: Array<ModuleEvent & { moduleName: string }> = [];

    if (!modules || !Array.isArray(modules)) {
      return events;
    }

    for (const module of modules) {
      if (!module || !module.events || !Array.isArray(module.events)) {
        continue;
      }

      for (const event of module.events) {
        events.push({
          ...event,
          moduleName: module.moduleName,
        });
      }
    }

    return events;
  }

  private getAllConstants(
    modules: ParsedModule[]
  ): Array<ModuleConstant & { moduleName: string }> {
    const constants: Array<ModuleConstant & { moduleName: string }> = [];

    if (!modules || !Array.isArray(modules)) {
      return constants;
    }

    for (const module of modules) {
      if (!module || !module.constants || !Array.isArray(module.constants)) {
        continue;
      }

      for (const constant of module.constants) {
        constants.push({
          ...constant,
          moduleName: module.moduleName,
        });
      }
    }

    return constants;
  }

  private capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  private logGenerated(filepath: string): void {
    const absolutePath = path.resolve(filepath);
    console.log(
      `${pc.bold(pc.green("[Brick]"))} Generated: ${path.basename(
        absolutePath
      )}`
    );
  }

  /**
   * Generates BrickModuleBase.kt interface
   */
  private async generateBrickModuleBase(outputDir: string): Promise<void> {
    const content = this.bridgeGenerator.generateBrickModuleBase();
    const outputPath = path.join(outputDir, "BrickModuleBase.kt");
    await fs.writeFile(outputPath, content);
  }

  /**
   * Generates the main Android bridge file (BrickModule.kt) - like iOS BrickModule.mm
   */
  private async generateAndroidBridge(
    modules: ParsedModule[],
    outputDir: string
  ): Promise<string> {
    const allMethodsRaw = this.getAllMethods(modules) || [];
    const allMethods = allMethodsRaw.map(({ method, module }) => ({
      ...method,
      moduleName: module.moduleName,
    }));
    const allConstants = this.getAllConstants(modules) || [];
    const allEvents = this.getAllEvents(modules) || [];

    const bridgeContent = this.bridgeGenerator.generateAndroidBridge(
      allMethods,
      allConstants,
      allEvents
    );

    const outputPath = path.join(outputDir, "BrickModule.kt");
    await fs.writeFile(outputPath, bridgeContent);
    this.logGenerated(outputPath);

    return outputPath;
  }

  /**
   * Generates library imports for data types used in BrickModuleImpl
   */
  private generateLibraryImports(modules: ParsedModule[]): string {
    return this.bridgeGenerator.generateLibraryImports(modules);
  }

  /**
   * Generates Kotlin implementation file (BrickModuleImpl.kt) - like iOS BrickModuleImpl.swift
   */
  private async generateKotlinImplementation(
    modules: ParsedModule[],
    outputDir: string
  ): Promise<string> {
    const allMethodsRaw = this.getAllMethods(modules);
    const allMethods = allMethodsRaw.map(({ method, module }) => ({
      ...method,
      moduleName: module.moduleName,
    }));
    const allConstants = this.getAllConstants(modules);
    const allEvents = this.getAllEvents(modules);
    const libraryImports = this.generateLibraryImports(modules);

    const implContent = this.bridgeGenerator.generateKotlinImplementation(
      allMethods,
      allConstants,
      allEvents,
      libraryImports
    );

    const outputPath = path.join(outputDir, "BrickModuleImpl.kt");
    await fs.writeFile(outputPath, implContent);
    this.logGenerated(outputPath);

    return outputPath;
  }

  /**
   * Extracts interface definitions from parsed module AST
   */
  private extractInterfaceDefinitions(module: ParsedModule): Array<{
    name: string;
    properties: Array<{ name: string; type: string; optional: boolean }>;
  }> {
    const interfaces: Array<{
      name: string;
      properties: Array<{ name: string; type: string; optional: boolean }>;
    }> = [];

    // Use AST-based interface definitions from parser.ts
    // This eliminates the need for regex parsing and provides better type safety
    for (const interfaceDef of module.interfaceDefinitions) {
      // Skip module spec interfaces and events interfaces
      if (
        interfaceDef.name.includes("ModuleSpec") ||
        interfaceDef.name.includes("Events")
      ) {
        continue;
      }

      const properties = interfaceDef.properties.map((prop) => ({
        name: prop.name,
        type: prop.type,
        optional: prop.optional,
      }));

      interfaces.push({
        name: interfaceDef.name,
        properties,
      });
    }

    return interfaces;
  }

  /**
   * Generates Kotlin data class from TypeScript interface definition
   */
  private generateKotlinDataClassFromInterface(interfaceDef: {
    name: string;
    properties: Array<{ name: string; type: string; optional: boolean }>;
  }): string {
    const className = interfaceDef.name;

    const dataClassProperties = interfaceDef.properties.map((prop) => {
      let kotlinType = this.mapTypeScriptToKotlin(prop.type);

      if (prop.optional && !kotlinType.endsWith("?")) {
        kotlinType += "?";
      }

      const defaultValue = prop.optional ? " = null" : "";

      return `    @SerializedName("${prop.name}")
    val ${prop.name}: ${kotlinType}${defaultValue}`;
    });

    return `data class ${className}(
${dataClassProperties.join(",\n")}
)`;
  }

  /**
   * Generates local module types in .brick/src/main/java/com/brickmodule/codegen/ directory
   */
  private async generateLocalModuleTypes(
    modules: ParsedModule[],
    outputDir: string
  ): Promise<string[]> {
    const generatedFiles: string[] = [];
    const typesDir = path.join(
      outputDir,
      "src",
      "main",
      "java",
      "com",
      "brickmodule",
      "codegen"
    );

    // Only process local modules (those without brickConfig)
    const localModules = modules.filter((m) => !m.sourceModule?.brickConfig);

    if (localModules.length === 0) {
      return generatedFiles;
    }

    // Ensure directory exists
    await fs.ensureDir(typesDir);

    for (const module of localModules) {
      // Set the module's package to com.brickmodule.codegen for local modules
      const originalModule = { ...module };
      if (!module.sourceModule.brickConfig) {
        module.sourceModule.brickConfig = {
          android: { package: "com.brickmodule.codegen" },
        };
      }

      // Generate interface file
      const interfaceContent = await this.generateLibraryInterface(module);
      const interfaceFile = path.join(typesDir, `${module.moduleName}Spec.kt`);
      await fs.writeFile(interfaceFile, interfaceContent);
      generatedFiles.push(interfaceFile);
      this.logGenerated(interfaceFile);

      // Generate types file if there are interface definitions
      if (module.interfaceDefinitions.length > 0) {
        const typesContent = await this.generateLibraryTypes(module);
        const typesFile = path.join(typesDir, `${module.moduleName}Types.kt`);
        await fs.writeFile(typesFile, typesContent);
        generatedFiles.push(typesFile);
        this.logGenerated(typesFile);
      }

      // Restore original module
      module.sourceModule.brickConfig = originalModule.sourceModule.brickConfig;
    }

    return generatedFiles;
  }

  /**
   * Generates Kotlin types for library mode (public API)
   */
  async generateLibraryTypes(module: ParsedModule): Promise<string> {
    const suite = createKotlinGeneratorSuite();
    return suite.generateDataClasses(module);
  }

  /**
   * Generates Kotlin interface for library mode (public API)
   */
  async generateLibraryInterface(module: ParsedModule): Promise<string> {
    const suite = createKotlinGeneratorSuite();
    const interfaceCode = suite.generateInterface(module);
    const eventHelpers =
      suite.deps.protocolGenerator.generateKotlinEventHelpers(module);
    return interfaceCode + eventHelpers;
  }
}
